// web/src/features/team/player/components/sections/RosterInfoSection.tsx
/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React from 'react'
import { TeamOutlined } from '@ant-design/icons'
import { useField } from 'formik'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { ScrollSpyLayout } from 'src/components/ScrollSpyLayout'
import { SwitchInput } from 'src/components/ts-components/SwitchInput'
import Text from 'src/components/ts-components/Text'
import {
  NumberField,
  SelectField,
  FieldRow,
} from 'src/features/profile/player/setupModal/components/FormikField'
import { usePositionOptions } from '../../hooks/useRosterPlayer'

const INJURED_NOTE = 'Player will not be able to play any games.'
const AFFILIATE_NOTE = 'Affiliates are out of the official roster.'
// Legacy affiliate note — kept alongside the new one per product.
const AFFILIATE_LEGACY_NOTE =
  'NOTE: Affiliate players will not receive Attendance notifications for this team or access to Team Chat.'

/**
 * A pill switch bound to a boolean Formik field, with a label and an
 * always-visible descriptive note. `subtitle` may be a node so the Affiliate
 * toggle can show both its notes stacked.
 */
const SwitchField: React.FC<{
  name: string
  label: string
  subtitle: React.ReactNode
}> = ({ name, label, subtitle }) => {
  const [field, , helpers] = useField<boolean>(name)
  return (
    <div css={switchRowStyle}>
      <SwitchInput
        title={label}
        subtitle={subtitle}
        value={Boolean(field.value)}
        onChange={(value) => helpers.setValue(value)}
      />
    </div>
  )
}

interface Props {
  sportId: string | null
}

const RosterInfoSection: React.FC<Props> = ({ sportId }) => {
  const positionOptions = usePositionOptions(sportId)
  const [affiliateField] = useField<boolean>('isAffiliate')

  return (
    <ScrollSpyLayout.Section
      id='rosterInfo'
      title={capitalizeWords('Roster Information')}
      icon={<TeamOutlined />}
    >
      <FieldRow cols={2}>
        {/* Numeric input — matches mobile's number-pad keyboard. Formik still
            holds the value as a string, so the payload transform is unchanged. */}
        <NumberField
          name='playerNumber'
          label='Player Number'
          placeholder='99'
          required
        />
        <SelectField
          name='positionId'
          label='Position'
          options={positionOptions}
          placeholder={capitalizeWords('Select a position')}
          required
        />
      </FieldRow>
      <FieldRow cols={2}>
        <SwitchField name='isInjured' label='Injured' subtitle={INJURED_NOTE} />
        <SwitchField
          name='isAffiliate'
          label='Affiliate'
          subtitle={
            <span css={affiliateNotesStyle}>
              <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
                {AFFILIATE_NOTE}
              </Text>
              {affiliateField.value ? (
                <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
                  {AFFILIATE_LEGACY_NOTE}
                </Text>
              ) : null}
            </span>
          }
        />
      </FieldRow>
      {/* Customer Identifier + Salesforce ID now live in Membership. */}
    </ScrollSpyLayout.Section>
  )
}

export default RosterInfoSection

// ---- styled ----
// SwitchInput renders a text block + the pill switch as siblings meant to sit
// in a flex row (label/notes on the left, switch on the right). Align both to
// the TOP (not vertically centered) so the Injured/Affiliate switches line up
// with each other regardless of how many note lines each cell has. The shared
// SwitchInput hardcodes `align-self: center` on its switch, so override it here
// and nudge it down to sit level with the label row.
const switchRowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  min-width: 0;

  .ant-switch {
    align-self: flex-start !important;
    margin-top: 2px;
  }
`

const affiliateNotesStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
`
